import { Link } from 'react-router-dom';
import { SEO, SchemaMarkup } from '../components/seo';
import { ServicesGrid } from '../components/ServicesGrid';
import { BrochureButton } from '../components/BrochureButton';
import { MessageSquare, Eye, Sparkles } from 'lucide-react';

const cards = [
  { color: 'bg-emerald-500', label: 'Groen', text: 'niet grensoverschrijdend' },
  { color: 'bg-amber-400', label: 'Geel', text: 'licht grensoverschrijdend' },
  { color: 'bg-orange-500', label: 'Oranje', text: 'behoorlijk grensoverschrijdend' },
  { color: 'bg-red-500', label: 'Rood', text: 'duidelijk grensoverschrijdend' },
];

const benefits = [
  { icon: MessageSquare, text: 'Een laagdrempelige manier om omgangsvormen bespreekbaar te maken' },
  { icon: Eye, text: 'Inzicht in hoe collega’s of betrokkenen situaties ervaren' },
  { icon: Sparkles, text: 'Bewustwording over grenzen en grijze gebieden' },
];

export function HetPuntUitSpel() {
  return (
    <>
      <SEO title="Het PuntUit-spel" description="Het PuntUit-spel maakt omgangsvormen bespreekbaar. Spelers reageren met groene, gele, oranje en rode kaarten op stellingen — laagdrempelig, constructief en leuk." canonicalUrl="/het-puntuit-spel" />
      <SchemaMarkup
        type="service"
        pageUrl="https://puntuit.nl/het-puntuit-spel"
        pageTitle="Het PuntUit-spel"
        pageDescription="Het PuntUit-spel maakt omgangsvormen bespreekbaar. Spelers reageren met groene, gele, oranje en rode kaarten op stellingen — laagdrempelig, constructief en leuk."
        breadcrumb={[
          { name: 'Home', url: '/' },
          { name: 'Het PuntUit-spel', url: '/het-puntuit-spel' },
        ]}
      />
      <div className="pt-28">
        <section className="bg-[#0284c7] text-white">
          <div className="container py-24 md:py-32 max-w-4xl">
            <p className="text-sm mb-4">Het PuntUit-spel</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Omgangsvormen bespreekbaar maken.</h1>
            <p className="text-xl text-primary-100 mb-6">Het PuntUit-spel helpt organisaties op een open, positieve, constructieve en vooral leuke manier het gesprek over omgangsvormen te voeren — zonder zware toon of verwijten.</p>
            <div className="flex gap-4 flex-wrap items-center">
              <Link to="/contact" className="btn-primary">Plan een kennismakingsgesprek</Link>
              <BrochureButton variant="white" />
            </div>
          </div>
        </section>

        {/* De kaarten */}
        <section className="section-padding bg-white">
          <div className="container max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="space-y-5 text-gray-700">
                <h2 className="text-3xl font-bold text-gray-900">Vier kleuren. Geen midden.</h2>
                <p>Spelers krijgen stellingen over herkenbare situaties. Per stelling kiezen zij een kaart die aangeeft in hoeverre zij iets grensoverschrijdend vinden.</p>
                <p className="font-medium text-gray-900">Er is geen neutrale middenoptie. Iedereen neemt positie in en dat opent het gesprek.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {cards.map((card) => (
                  <div key={card.label} className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden bg-white">
                    <div className={`${card.color} h-20`} />
                    <div className="p-5">
                      <p className="font-semibold text-gray-900">{card.label}</p>
                      <p className="text-gray-600 text-sm">{card.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Wat het spel oplevert */}
        <section className="section-padding bg-gray-50">
          <div className="container max-w-6xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Wat het spel oplevert.</h2>
            <p className="text-gray-700 mb-10 max-w-2xl">Zo zet je het onderwerp op een positieve manier op de kaart — want voorkomen is beter dan genezen.</p>
            <div className="grid md:grid-cols-3 gap-6">
              {benefits.map(({ icon: Icon, text }) => (
                <div key={text} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-[#0284c7]/10 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#0284c7]" />
                  </div>
                  <p className="text-gray-700">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Levering en begeleiding */}
        <section className="section-padding bg-white">
          <div className="container max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-5">Levering en begeleiding.</h2>
            <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
              <p>Het spel is onderdeel van het abonnement en zo ingericht dat organisaties het gemakkelijk zelf kunnen inzetten.</p>
              <p>Daarnaast kan men kiezen voor live begeleiding door een vertrouwenspersoon van PuntUit tijdens een sessie op locatie, eventueel in combinatie met een live introductie van PuntUit, tegen uurtarief.</p>
            </div>
          </div>
        </section>

        <ServicesGrid />
      </div>
    </>
  );
}
